// Timestop BO — Web Push subscription helpers.
//
// Phase 2 of docs/pwa-plan.md. Wires the browser's PushManager
// into the BO so a logged-in user can opt in to notifications
// from the Profile modal. The matching server-side is in
// admin/api/Push.php (Phase 2) and admin/api/index.php's
// push_vapid_key / push_subscribe / push_unsubscribe /
// push_subscriptions cases.
//
// Lifecycle (managed by this module):
//   1. ensurePushReady() — fetch the server's VAPID public key,
//      cache it, and stash the WebPush feature-detection result
//      on state.pwa. Called once at bundle init (after login)
//      and on every page load so the Profile modal can read
//      state.pwa.canSubscribe without a round-trip.
//   2. subscribeToPush() — request browser permission, build
//      the PushSubscription via PushManager.subscribe(), POST
//      the resulting endpoint+keys to /api/push_subscribe.
//      Returns {ok, error?}. The SPA shows the result inline.
//   3. unsubscribeFromPush() — call
//      registration.pushManager.getSubscription() (if any),
//      .unsubscribe() client-side, then POST the endpoint to
//      /api/push_unsubscribe so the server row is removed.
//      Returns {ok, error?}. Idempotent — calling twice is
//      fine.
//   4. listMyPushSubscriptions() — GET /api/push_subscriptions
//      and return the rows. Used by the Profile modal's
//      notification section to show "N devices subscribed" +
//      a (future) per-device remove list.
//
// Permission model:
//   - The browser's Notification.permission can be 'default',
//     'granted', or 'denied'. Once denied, the browser
//     remembers — the user has to flip the site permission
//     manually in their browser settings. The Profile modal
//     shows a "Notifications are blocked" hint in that state
//     (see openProfileModal's notification hint).
//   - requestPermission() returns a Promise<NotificationPermission>.
//     Resolves immediately if the user has already chosen.
//
// State machine for state.pwa (new, attached to TimestopState by
// attachPushState below):
//   canSubscribe: boolean | null
//     null = feature detect not yet run
//     true = browser has PushManager + Notification
//     false = at least one of the two is missing (hide UI)
//   permission: NotificationPermission | 'unknown'
//     Mirrors Notification.permission, refreshed on
//     ensurePushReady() and after subscribe/unsubscribe.
//   subscribed: boolean
//     True iff the server has at least one active
//     subscription for the current user (from
//     /api/push_subscriptions). The Profile modal uses this
//     to render "Subscribe" vs "Unsubscribe" button state.
//   publicKey: string | null
//     Cached VAPID public key. Passed to
//     PushManager.subscribe() as applicationServerKey.
//   loading: boolean
//     True while a subscribe/unsubscribe is in flight. The
//     button is disabled during this window so a double-
//     click doesn't enqueue two POSTs.

import { apiFetch, type ApiFetchConfig } from './api';
import type { TimestopState } from './state';

// =====================================================================
// State shape (attached to TimestopState by attachPushState below)
// =====================================================================

export interface PushState {
    canSubscribe: boolean | null;
    permission: NotificationPermission | 'unknown';
    subscribed: boolean;
    publicKey: string | null;
    loading: boolean;
}

export const INITIAL_PUSH_STATE: PushState = {
    canSubscribe: null,
    permission: 'unknown',
    subscribed: false,
    publicKey: null,
    loading: false,
};

// =====================================================================
// Feature detection
// =====================================================================

// Promise timeout helper. Used in subscribeToPush() to bail out
// when the browser's PushManager hangs (Firefox in particular can
// do this on a partially-updated SW). The native Promise.race
// with a setTimeout-rejected promise forces a rejection after the
// deadline, which the caller's try/catch converts into a
// structured error the user can see in the red text below the
// button.
function raceWithTimeout<T>(p: Promise<T>, ms: number, code: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(code)), ms);
        p.then(
            (v) => { clearTimeout(t); resolve(v); },
            (e) => { clearTimeout(t); reject(e); }
        );
    });
}

// PushManager + Notification both have to be available. In
// practice: Chrome/Edge/Firefox/Safari 16.4+ all have both.
// Older Safari + iOS WebViews (in some Android browsers) lack
// PushManager even though they have Notification. We don't try
// to be cleverer than that — the standard says "use PushManager
// + Notification together or not at all".
function canUsePush(): boolean {
    if (typeof window === 'undefined') return false;
    if (!('serviceWorker' in navigator)) return false;
    if (!('PushManager' in window)) return false;
    if (!('Notification' in window)) return false;
    return true;
}

function currentPermission(): NotificationPermission | 'unknown' {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unknown';
    return Notification.permission as NotificationPermission;
}

// =====================================================================
// Server VAPID public key
// =====================================================================

// Cache the public key for the lifetime of the page. The key is
// a constant on the server (one per VAPID keypair), so re-
// fetching is wasteful. We bust the cache only on the rare
// "config changed" event (future; nothing does this yet).
let _cachedPublicKey: string | null = null;

async function fetchVapidPublicKey(config: ApiFetchConfig): Promise<string> {
    if (_cachedPublicKey) return _cachedPublicKey;
    const r = await apiFetch('push_vapid_key', { method: 'GET' }, config);
    const data = r.data as { ok?: boolean; publicKey?: string; error?: string } | null;
    if (r.ok && data && data.ok && data.publicKey) {
        _cachedPublicKey = data.publicKey;
        return _cachedPublicKey;
    }
    const errCode = (data && data.error) || 'unknown';
    throw new Error('vapid_key_unavailable:' + errCode);
}

// =====================================================================
// State attachment
// =====================================================================

// Attach the PushState to the shared TimestopState container.
// Idempotent — does nothing if state.pwa is already set.
export function attachPushState(state: TimestopState): void {
    if (!state.pwa) {
        // Cast: TimestopState is augmented at runtime via a
        // window.timestop.state mutation. The TS interface isn't
        // updated to keep the type narrow; this is a
        // deliberate "use unknown" for the legacy fields.
        (state as unknown as { pwa: PushState }).pwa = { ...INITIAL_PUSH_STATE };
    }
}

// =====================================================================
// One-time bootstrap
// =====================================================================

// Fetch the VAPID key + run feature detection. Called from
// doLogin (after a fresh login) and from the bundle's page-
// load bootstrap (existing session). Idempotent.
export async function ensurePushReady(state: TimestopState, config: ApiFetchConfig): Promise<void> {
    attachPushState(state);
    const pwa = (state as unknown as { pwa: PushState }).pwa;
    pwa.canSubscribe = canUsePush();
    pwa.permission = currentPermission();
    if (!pwa.canSubscribe) {
        // No point fetching the VAPID key on a host that
        // can't use push. The Profile modal's notification
        // section will read pwa.canSubscribe === false and
        // render a "your browser doesn't support push"
        // fallback.
        return;
    }
    // VAPID key fetch is best-effort. If the server hasn't
    // generated a keypair yet, pwa.publicKey stays null and
    // the UI hides the subscribe button. We swallow the
    // error here so a server-side config gap doesn't break
    // the page.
    try {
        const k = await fetchVapidPublicKey(config);
        pwa.publicKey = k;
    } catch (e) {
        console.warn('[timestop] VAPID key fetch failed:', e);
        pwa.publicKey = null;
    }
    // Best-effort: fetch the user's existing subscriptions so
    // the Profile modal can show the right initial state. If
    // the table isn't migrated yet, listUserSubscriptions
    // returns [] gracefully and the UI shows "Subscribe".
    try {
        const r = await apiFetch('push_subscriptions', { method: 'GET' }, config);
        const data = r.data as { count?: number } | null;
        pwa.subscribed = !!(r.ok && data && (data.count ?? 0) > 0);
    } catch (e) {
        pwa.subscribed = false;
    }
}

// =====================================================================
// Subscribe / unsubscribe
// =====================================================================

// Convert a base64url string (as returned by
// PushManager's subscription.getKey()) to a Uint8Array the
// PushManager.subscribe() call expects. The browser's
// subscription.getKey('p256dh') returns an ArrayBuffer;
// the server expects a base64url string, so the round-trip
// is: ArrayBuffer -> base64url (server side, in subscribe()),
// and base64url -> Uint8Array (server side, in
// sendToSubscription() / web-push lib).
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

// Subscribe to push. Caller is responsible for the user
// gesture (e.g. button click); PushManager.subscribe() is
// gated by the spec to only work in response to a user
// gesture, so a programmatic call after async-await may
// fail silently in some browsers.
export async function subscribeToPush(state: TimestopState, config: ApiFetchConfig): Promise<{ ok: boolean; error?: string }> {
    attachPushState(state);
    const pwa = (state as unknown as { pwa: PushState }).pwa;
    if (!pwa.canSubscribe) return { ok: false, error: 'push_not_supported' };
    if (!pwa.publicKey) return { ok: false, error: 'vapid_key_unavailable' };
    if (pwa.loading) return { ok: false, error: 'already_loading' };
    pwa.loading = true;
    try {
        // Step 1: ask the user for permission. The browser
        // resolves with the new permission; if it was already
        // 'granted' the promise resolves immediately to
        // 'granted'. If it was 'denied' it resolves to
        // 'denied' without re-prompting.
        const perm = await Notification.requestPermission();
        pwa.permission = perm;
        if (perm !== 'granted') {
            return { ok: false, error: perm === 'denied' ? 'permission_denied' : 'permission_default' };
        }
        // Step 2: get the SW registration. If the SW isn't
        // active yet, wait for it. The PWA bootstrap
        // registers sw.js on load, but a user who clicks
        // "Enable notifications" before the SW is fully
        // activated would otherwise get a NoActiveSW error.
        // We also bound this wait with a timeout: in practice
        // serviceWorker.ready should resolve in <1s once the
        // SW is installed. If it doesn't, the SW is broken or
        // never installed at all -- bail out with a clear
        // error instead of hanging forever.
        const reg = await raceWithTimeout(
            navigator.serviceWorker.ready,
            8000,
            'service_worker_not_ready'
        );
        // Step 3: subscribe. userVisibleOnly:true is required
        // by the spec; applicationServerKey must be the
        // server's VAPID public key as a Uint8Array. The
        // PushManager typing in lib.dom.d.ts is strict about
        // ArrayBuffer (not SharedArrayBuffer) so we cast
        // through `unknown` to satisfy the type checker; the
        // runtime value is a normal Uint8Array.
        //
        // Also wrapped in a timeout. Firefox in particular can
        // hang on pushManager.subscribe() when the SW is
        // partially-updated (e.g. the v1 SW is still serving
        // the page while v2 has installed in the background)
        // -- the call neither resolves nor rejects. The 10s
        // timeout forces a hard fail so the user can retry
        // after a hard-reload rather than see a silent hang.
        const sub = await raceWithTimeout(
            reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(pwa.publicKey) as unknown as BufferSource,
            }),
            10000,
            'push_subscribe_timeout'
        );
        // Step 4: POST to /api/push_subscribe. The server
        // upserts the row keyed on endpoint uniqueness.
        const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
        const r = await apiFetch('push_subscribe',
            {
                method: 'POST',
                body: {
                    endpoint: json.endpoint,
                    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
                    contentEncoding: 'aesgcm',
                },
            },
            config,
        );
        if (!r.ok) {
            const data = r.data as { error?: string } | null;
            const errCode = (data && data.error) || 'subscribe_failed';
            return { ok: false, error: errCode };
        }
        pwa.subscribed = true;
        return { ok: true };
    } catch (e) {
        return { ok: false, error: 'exception:' + (e as Error).message };
    } finally {
        pwa.loading = false;
    }
}

export async function unsubscribeFromPush(state: TimestopState, config: ApiFetchConfig): Promise<{ ok: boolean; error?: string }> {
    attachPushState(state);
    const pwa = (state as unknown as { pwa: PushState }).pwa;
    if (!pwa.canSubscribe) return { ok: false, error: 'push_not_supported' };
    if (pwa.loading) return { ok: false, error: 'already_loading' };
    pwa.loading = true;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
            // No active client-side subscription. The server
            // may still have a row (browser cleared its
            // subscription on a permission flip without
            // notifying us). Best-effort: also call
            // /api/push_unsubscribe with whatever endpoint we
            // can find, which will be a no-op.
            pwa.subscribed = false;
            return { ok: true };
        }
        const endpoint = sub.endpoint;
        // Client-side unsubscribe FIRST. If the network call
        // to /api/push_unsubscribe fails, the client still
        // stops receiving pushes; the server row will be
        // cleaned up by the dispatcher's 404/410 path the
        // next time the dispatcher tries to deliver.
        await sub.unsubscribe();
        const r = await apiFetch('push_unsubscribe',
            { method: 'POST', body: { endpoint } },
            config,
        );
        // Even on a 4xx the client-side unsubscribe
        // succeeded, so the local state is "not subscribed".
        pwa.subscribed = false;
        if (!r.ok) {
            const data = r.data as { error?: string } | null;
            const errCode = (data && data.error) || 'unsubscribe_failed';
            // We return ok:true because the local unsubscribe
            // worked; the errCode is informational (for
            // logging / a future toast).
            return { ok: true, error: errCode };
        }
        return { ok: true };
    } catch (e) {
        return { ok: false, error: 'exception:' + (e as Error).message };
    } finally {
        pwa.loading = false;
    }
}

// =====================================================================
// Phase 2 hook: chain push unsubscribe on logout.
// =====================================================================

// The auth.doLogout() path goes through window.doLogout (in the
// inline script). The bundle's doLogout in auth.ts doesn't
// know about push. We bridge by exporting a best-effort
// unsubscribe call that logout callers can fire-and-forget.
// The 5s timeout is generous — /api/push_unsubscribe is
// almost always < 100ms — and prevents a stuck network from
// blocking the page-reload.
export async function logoutUnsubscribePush(state: TimestopState, config: ApiFetchConfig): Promise<void> {
    attachPushState(state);
    const pwa = (state as unknown as { pwa: PushState }).pwa;
    if (!pwa || !pwa.subscribed) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
            pwa.subscribed = false;
            return;
        }
        // Race the unsubscribe + the server POST against a
        // 5s timeout. The page reloads on its own schedule
        // (see auth.ts doLogout); we don't want to delay it.
        const endpoint = sub.endpoint;
        await Promise.race([
            (async () => {
                await sub.unsubscribe();
                await apiFetch('push_unsubscribe',
                    { method: 'POST', body: { endpoint }, keepalive: true },
                    config);
            })(),
            new Promise<void>((resolve) => setTimeout(resolve, 5000)),
        ]);
        pwa.subscribed = false;
    } catch (e) {
        // Best-effort. A failed unsubscribe on logout just
        // means the server row stays around until the
        // dispatcher's 404/410 path cleans it up.
        console.warn('[timestop] logout push unsubscribe failed:', e);
    }
}
